import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
    RefreshCcw, 
    Download, 
    Search, 
    TrendingUp, 
    Users, 
    UserPlus, 
    Zap, 
    Globe, 
    ArrowUpRight,
    ArrowDownRight,
    MousePointer2,
    Activity,
    Target
} from 'lucide-react';
import { 
    analyticsApi,
    triggersApi,
    type AnalyticsEvent,
    type DashboardMetrics,
    type MetricSummary,
    type Trigger,
} from '../../lib/api/analytics';
import { LiveWorldMap } from '../../features/analytics/components/LiveWorldMap';
import { RealtimeDashboard } from '../../features/analytics/components/RealtimeDashboard';
import { useAnalyticsWebSocket } from '../../features/analytics/hooks/useAnalyticsWebSocket';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

// ==================== SUB-COMPONENTS ====================

const StatCard: React.FC<{
    title: string;
    value: string | number;
    change?: number;
    description?: string;
    icon: React.ReactNode;
    loading?: boolean;
}> = ({ title, value, change, description, icon, loading }) => (
    <Card className="hover:shadow-md transition-all border-neutral-100 shadow-sm overflow-hidden group">
        <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-neutral-50 rounded-lg text-neutral-500 group-hover:bg-neutral-900 group-hover:text-white transition-colors duration-300">
                    {icon}
                </div>
                {change !== undefined && (
                    <div className={cn(
                        "flex items-center text-xs font-bold px-2 py-0.5 rounded-full",
                        change >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
                    )}>
                        {change >= 0 ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                        {Math.abs(change)}%
                    </div>
                )}
            </div>
            {loading ? (
                <div className="space-y-2">
                    <div className="h-4 w-20 bg-neutral-100 animate-pulse rounded" />
                    <div className="h-8 w-24 bg-neutral-100 animate-pulse rounded" />
                </div>
            ) : (
                <>
                    <p className="text-sm font-medium text-neutral-500 mb-1">{title}</p>
                    <h3 className="text-2xl font-black text-neutral-900">{value}</h3>
                    {description && (
                        <p className="text-[10px] text-neutral-400 font-medium mt-1 uppercase tracking-tight">{description}</p>
                    )}
                </>
            )}
        </CardContent>
    </Card>
);

// ==================== MAIN PAGE ====================

type TabType = 'realtime' | 'overview' | 'events' | 'metrics' | 'triggers';

export const AnalyticsPage: React.FC = () => {
    const { connected, recentEvents, activeVisitors, todayStats, refresh: refreshWS } = useAnalyticsWebSocket();
    const [activeTab, setActiveTab] = useState<TabType>('realtime');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [overviewData, setOverviewData] = useState<DashboardMetrics | null>(null);
    const [events, setEvents] = useState<AnalyticsEvent[]>([]);
    const [triggers, setTriggers] = useState<Trigger[]>([]);
    const [metricSummary, setMetricSummary] = useState<MetricSummary | null>(null);

    // Filters
    const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');
    const [searchQuery, setSearchQuery] = useState('');

    const fetchOverviewData = async () => {
        try {
            const data = await analyticsApi.getDashboardMetrics(parseInt(dateRange) || 30);
            setOverviewData(data);
        } catch (error) {
            console.error('Failed to fetch overview data:', error);
        }
    };

    const fetchEvents = async () => {
        try {
            const data = await analyticsApi.getEvents({ limit: 50 });
            setEvents(data || []);
        } catch (error) {
            console.error('Failed to fetch events:', error);
        }
    };

    const fetchTriggers = async () => {
        try {
            const data = await triggersApi.getTriggers();
            setTriggers(data || []);
        } catch (error) {
            console.error('Failed to fetch triggers:', error);
        }
    };

    const fetchMetricSummary = async () => {
        try {
            const data = await analyticsApi.getMetricSummary('signups'); // Specific metric needed now
            setMetricSummary(data);
        } catch (error) {
            console.error('Failed to fetch metric summary:', error);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([
            fetchOverviewData(),
            activeTab === 'events' && fetchEvents(),
            activeTab === 'triggers' && fetchTriggers(),
            activeTab === 'metrics' && fetchMetricSummary(),
            activeTab === 'realtime' && fetchOverviewData(),
            refreshWS(),
        ]);
        setRefreshing(false);
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await handleRefresh();
            setLoading(false);
        };
        init();
    }, [dateRange]);

    useEffect(() => {
        if (activeTab === 'events') fetchEvents();
        if (activeTab === 'triggers') fetchTriggers();
        if (activeTab === 'metrics') fetchMetricSummary();
    }, [activeTab]);

    return (
        <div className="flex flex-col gap-6 p-6 max-w-[1400px] mx-auto animate-in fade-in duration-500">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <h1 className="text-3xl font-black text-neutral-900 tracking-tight">Analytics</h1>
                    </div>
                    <p className="text-sm text-neutral-400 font-medium">Monitor your metrics and visitor activities in real-time.</p>
                </div>

                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-9 font-bold bg-white text-neutral-600 hover:text-neutral-900 border-neutral-100 shadow-sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        <RefreshCcw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
                        Refresh
                    </Button>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-9 w-9 text-neutral-400 hover:text-neutral-900 border border-neutral-100 bg-white"
                        title="Export Data"
                    >
                        <Download className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Navigation & Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex bg-neutral-100/50 p-1 rounded-lg border border-neutral-100">
                    {[
                        { id: 'realtime', label: 'Realtime', icon: <Zap size={14} /> },
                        { id: 'overview', label: 'Overview', icon: <Globe size={14} /> },
                        { id: 'metrics', label: 'Metrics', icon: <TrendingUp size={14} /> },
                        { id: 'events', label: 'Events', icon: <Activity size={14} /> },
                        { id: 'triggers', label: 'Triggers', icon: <Target size={14} /> },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as TabType)}
                            className={cn(
                                "flex items-center gap-2 px-4 py-1.5 text-xs font-bold rounded-md transition-all",
                                activeTab === tab.id 
                                    ? "bg-white text-neutral-900 shadow-sm" 
                                    : "text-neutral-400 hover:text-neutral-600"
                            )}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2 bg-neutral-100/50 p-1 rounded-lg border border-neutral-100">
                    {[
                        { id: '7d', label: '7 Days' },
                        { id: '30d', label: '30 Days' },
                        { id: '90d', label: '90 Days' },
                    ].map((range) => (
                        <button
                            key={range.id}
                            onClick={() => setDateRange(range.id as any)}
                            className={cn(
                                "px-3 py-1.5 text-xs font-bold rounded-md transition-all",
                                dateRange === range.id 
                                    ? "bg-white text-neutral-900 shadow-sm" 
                                    : "text-neutral-400 hover:text-neutral-600"
                            )}
                        >
                            {range.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content Viewport */}
            <div className="relative min-h-[600px]">
                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-[1px] z-10 rounded-2xl">
                        <div className="flex flex-col items-center gap-3">
                            <span className="relative flex h-10 w-10">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-20"></span>
                                <span className="relative inline-flex rounded-full h-10 w-10 bg-blue-600 items-center justify-center">
                                    <TrendingUp className="text-white w-5 h-5" />
                                </span>
                            </span>
                            <span className="text-xs font-black text-neutral-400 uppercase tracking-widest">Preloading Data...</span>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        {/* Tab Content Rendering */}
                        <>
                            {/* REALTIME TAB */}
                            {activeTab === 'realtime' && (
                                <RealtimeDashboard 
                                    connected={connected}
                                    activeVisitors={activeVisitors}
                                    recentEvents={recentEvents}
                                    todayStats={todayStats}
                                />
                            )}

                            {/* OVERVIEW TAB */}
                            {activeTab === 'overview' && overviewData && (
                                <div className="flex flex-col gap-6 animate-in slide-in-from-bottom-2 duration-500">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <StatCard 
                                            title="Active Users"
                                            value={overviewData.metrics.logins.current}
                                            change={overviewData.metrics.logins.change}
                                            description="Active participants"
                                            icon={<Users size={18} />}
                                            loading={loading}
                                        />
                                        <StatCard 
                                            title="Boards Created"
                                            value={overviewData.metrics.boardsCreated.current}
                                            change={overviewData.metrics.boardsCreated.change}
                                            description="Engagement metric"
                                            icon={<Activity size={18} />}
                                            loading={loading}
                                        />
                                        <StatCard 
                                            title="New Signups"
                                            value={overviewData.metrics.signups.current}
                                            change={overviewData.metrics.signups.change}
                                            description="User acquisition"
                                            icon={<UserPlus size={18} />}
                                            loading={loading}
                                        />
                                        <StatCard 
                                            title="Revenue"
                                            value={`$${overviewData.metrics.revenue.current.toLocaleString()}`}
                                            change={overviewData.metrics.revenue.change}
                                            description="Financial growth"
                                            icon={<TrendingUp size={18} />}
                                            loading={loading}
                                        />
                                    </div>
                                    
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <Card className="border-neutral-100 shadow-sm overflow-hidden min-h-[400px]">
                                            <CardHeader className="pb-2">
                                                <CardTitle className="text-base font-bold flex items-center gap-2">
                                                    <Globe className="w-4 h-4 text-neutral-400" />
                                                    Global Reach
                                                </CardTitle>
                                                <CardDescription className="text-xs">Visitor distribution by country</CardDescription>
                                            </CardHeader>
                                            <CardContent className="h-[350px] flex items-center justify-center">
                                                {/* Reusing LiveWorldMap for historical data if needed, or static stats */}
                                                <LiveWorldMap visitors={activeVisitors} />
                                            </CardContent>
                                        </Card>
                                        
                                        <Card className="border-neutral-100 shadow-sm overflow-hidden">
                                           <CardHeader className="pb-2">
                                                <CardTitle className="text-base font-bold flex items-center gap-2">
                                                    <MousePointer2 className="w-4 h-4 text-neutral-400" />
                                                    Top Pages
                                                </CardTitle>
                                                <CardDescription className="text-xs">Most visited pages this period</CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    {[
                                                        { path: '/', views: '2.4k', rate: 45 },
                                                        { path: '/dashboard', views: '1.8k', rate: 32 },
                                                        { path: '/pricing', views: '950', rate: 12 },
                                                        { path: '/docs', views: '420', rate: 8 }
                                                    ].map((page, i) => (
                                                        <div key={i} className="flex flex-col gap-1">
                                                            <div className="flex justify-between text-xs font-bold mb-1">
                                                                <span className="text-neutral-600">{page.path}</span>
                                                                <span className="text-neutral-400">{page.views}</span>
                                                            </div>
                                                            <div className="h-1.5 w-full bg-neutral-50 rounded-full overflow-hidden">
                                                                <div 
                                                                    className="h-full bg-blue-600 rounded-full" 
                                                                    style={{ width: `${page.rate}%` }} 
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            )}

                            {/* EVENTS TAB - List View */}
                            {activeTab === 'events' && (
                                <Card className="border-neutral-100 shadow-sm animate-in slide-in-from-bottom-2 duration-500">
                                    <div className="p-4 border-b border-neutral-50 flex items-center justify-between">
                                        <div className="relative flex-1 max-w-sm">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                                            <Input 
                                                placeholder="Search events..." 
                                                className="pl-9 h-9 border-neutral-100 bg-neutral-50/50"
                                                value={searchQuery}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <CardContent className="p-0">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left text-sm">
                                                <thead>
                                                    <tr className="bg-neutral-50/50 text-[10px] uppercase tracking-widest text-neutral-400 font-black">
                                                        <th className="px-6 py-4">Status</th>
                                                        <th className="px-6 py-4">Event</th>
                                                        <th className="px-6 py-4">User</th>
                                                        <th className="px-6 py-4">Source</th>
                                                        <th className="px-6 py-4 text-right">Time</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-neutral-50">
                                                    {events.length > 0 ? events.map((event, i) => (
                                                        <tr key={i} className="hover:bg-neutral-50/30 transition-colors group">
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-2">
                                                                    <div className={cn(
                                                                        "size-1.5 rounded-full animate-pulse",
                                                                        event.type === 'error' ? 'bg-rose-500' : 'bg-blue-500'
                                                                    )} />
                                                                    <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-tighter">Live</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex flex-col">
                                                                     <span className="font-bold text-neutral-900 group-hover:text-blue-600 transition-colors uppercase text-[11px]">{event.type || 'unknown'}</span>
                                                                    <span className="text-[10px] text-neutral-400 truncate max-w-[200px]">{JSON.stringify(event.properties)}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className="font-medium text-neutral-600 tabular-nums">{event.userId || (event.anonymousId?.slice(0, 8) || 'anon')}</span>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className="text-xs bg-neutral-100 px-2 py-0.5 rounded text-neutral-500 font-bold">{event.context?.os || 'Unknown'}</span>
                                                            </td>
                                                            <td className="px-6 py-4 text-right text-neutral-400 tabular-nums">
                                                                {format(new Date(event.timestamp), 'HH:mm:ss')}
                                                            </td>
                                                        </tr>
                                                    )) : (
                                                        <tr>
                                                            <td colSpan={5} className="px-6 py-12 text-center text-neutral-400 font-medium">No events found matching your criteria.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* TRIGGERS & METRICS PLOTS - Coming Soon placeholders if data missing */}
                            {((activeTab === 'triggers' && triggers) || (activeTab === 'metrics' && metricSummary)) && (
                                <div className="flex flex-col items-center justify-center min-h-[400px] bg-white border border-neutral-100 border-dashed rounded-2xl gap-4">
                                    <div className="p-4 bg-blue-50 rounded-full text-blue-600">
                                        <TrendingUp size={32} />
                                    </div>
                                    <h4 className="text-lg font-bold text-neutral-900">Advanced {activeTab} View</h4>
                                    <p className="text-sm text-neutral-400 max-w-sm text-center">We're collecting more data before showing these advanced trends. Check back in a few hours.</p>
                                    <Button variant="outline" size="sm" onClick={() => setActiveTab('overview')}>Back to Overview</Button>
                                </div>
                            )}
                        </>
                    </div>
                )}
            </div>
        </div>
    );
};
